import { useCallback, useState } from 'react';
import axios from 'axios';
import { parseAndDownloadBinaryFileBlob } from '../../../../../components/FileUpload/utils';
import { largeFileDownload, largeFileUpload } from '../../../../../services/api/largeFileHandling/largeFileHandling';
import {
  deleteCentralDocumentHandlerStipulationDocument,
  getCentralDocumentHandlerStipulationDocument,
  getCentralDocumentHandlerStipulationDocuments,
  postCentralDocumentHandlerStipulationDocument,
} from '../../../../../services/api/miles/central-document-handling';
import { StipulationItem } from '../../../../../types/dashboardDocuments';
import {
  DashboardSubpageDocumentsEntityType,
  DashboardSubpageDocumentsSourceType,
} from '../../DashboardSubpageDocuments/DashboardSubpageDocuments';

export const useCentralDocumentHandling = (
  id: string,
  sourceType: DashboardSubpageDocumentsSourceType,
  entityType: DashboardSubpageDocumentsEntityType
) => {
  const [stipulations, setStipulations] = useState<StipulationItem[]>([]);
  const [coApplicantStipulations, setCoApplicantStipulations] = useState<StipulationItem[]>([]);
  const [creditApplicationCoApplicantId, setCreditApplicationCoApplicantId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState('');

  const getStipulationList = useCallback(async () => {
    setLoadingId('stipulations-list');
    try {
      const {
        stipulations: stipulationDocuments,
        coApplicantStipulations: coStipulations,
        creditApplicationCoApplicantId: coApplicantId,
      } = await getCentralDocumentHandlerStipulationDocuments(id, sourceType, entityType);
      setStipulations(stipulationDocuments);
      if (coApplicantId) {
        setCoApplicantStipulations(coStipulations ?? []);
        setCreditApplicationCoApplicantId(coApplicantId);
      }
      setLoadingId('');
    } catch (error) {
      setLoadingId('');
    }
  }, [sourceType, entityType, id]);

  const uploadStipulationDocument = useCallback(
    async (stipulationId: string, file: File, creditCoApplicantId?: string) => {
      setLoadingId(`document-upload-${stipulationId}`);

      const fileRequest: MediaStorageService.Schemas.FilesRequestPost = {
        file: {
          name: file.name,
          mimeType: file.type,
          expirationDays: 1,
          createS3Uri: true,
          fileSize: file.size,
          url: {
            ttl: 30,
          },
        },
      };

      try {
        const mediaStorageUploadRes = await largeFileUpload(fileRequest);

        const { url } = mediaStorageUploadRes.file;

        if (url?.temporalUrl) {
          await axios.put(url?.temporalUrl, file, {
            headers: {
              'Content-Type': file.type,
            },
          });

          const res = await postCentralDocumentHandlerStipulationDocument(
            id,
            stipulationId,
            sourceType,
            entityType,
            {
              id: mediaStorageUploadRes.file.id,
              mimeType: mediaStorageUploadRes.file.mimeType,
            },
            creditCoApplicantId
          );

          // Validate response structure
          if (!res?.documents?.[0]?.documentId) {
            throw new Error('Invalid response: missing document ID');
          }

          let stipulationsCollection = stipulations;
          if (creditCoApplicantId) {
            stipulationsCollection = coApplicantStipulations;
          }

          const stipulationIndex = stipulationsCollection.findIndex((stipulation) => stipulation.id === stipulationId);
          if (stipulationIndex !== -1) {
            const newStipulations = [...stipulationsCollection];
            newStipulations[stipulationIndex] = {
              ...stipulationsCollection[stipulationIndex],
              documents: [
                ...stipulationsCollection[stipulationIndex].documents,
                { documentId: res.documents[0].documentId, description: `${file.name.split('.')[0]}.pdf` },
              ],
            };
            if (creditCoApplicantId) {
              setCoApplicantStipulations(newStipulations);
            } else {
              setStipulations(newStipulations);
            }
          }

          setLoadingId('');
          return res;
        }
        // return res; // Return response for caller to handle success
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Upload failed:', error);
        setLoadingId('');
        throw error; // Re-throw for caller to handle error
      }
      return 'OK';
    },
    [stipulations, coApplicantStipulations, sourceType, entityType, id]
  );

  const getStipulationDocument = useCallback(
    async (stipulationId: string, documentId: string, documentName: string, creditCoApplicantId?: string) => {
      setLoadingId(`document-get-${documentId}`);
      try {
        const { url } = await getCentralDocumentHandlerStipulationDocument(
          id,
          stipulationId,
          documentId,
          sourceType,
          entityType,
          creditCoApplicantId
        );

        const response = await largeFileDownload(url);
        await parseAndDownloadBinaryFileBlob(documentName, response.data);
        setLoadingId('');
      } catch (error) {
        setLoadingId('');
      }
    },
    [sourceType, entityType, id]
  );

  const deleteStipulationDocument = useCallback(
    async (stipulationId: string, documentId: string, creditCoApplicantId?: string) => {
      setLoadingId(`document-delete-${documentId}`);
      try {
        await deleteCentralDocumentHandlerStipulationDocument(
          id,
          stipulationId,
          documentId,
          sourceType,
          entityType,
          creditCoApplicantId
        );

        let stipulationsCollection = stipulations;
        if (creditCoApplicantId) {
          stipulationsCollection = coApplicantStipulations;
        }

        const stipulationIndex = stipulationsCollection.findIndex((stipulation) => stipulation.id === stipulationId);
        if (stipulationIndex !== -1) {
          const newStipulations = [...stipulationsCollection];
          const docs = newStipulations[stipulationIndex].documents.filter((doc) => doc.documentId !== documentId);
          newStipulations[stipulationIndex] = {
            ...stipulationsCollection[stipulationIndex],
            documents: docs,
          };

          if (creditCoApplicantId) {
            setCoApplicantStipulations(newStipulations);
          } else {
            setStipulations(newStipulations);
          }

          setLoadingId('');
        }
      } catch (error) {
        setLoadingId('');
      }
    },
    [stipulations, coApplicantStipulations, sourceType, entityType, id]
  );

  return {
    stipulations,
    coApplicantStipulations,
    creditApplicationCoApplicantId,
    loadingId,
    getStipulationList,
    getStipulationDocument,
    uploadStipulationDocument,
    deleteStipulationDocument,
  };
};
